import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
// Subpath import (same object as antd's Layout) so the module stays loadable
// under bun test, which can't resolve `Layout` from antd's CJS root.
import Layout from 'antd/lib/layout';
import {
  ExceptionOutlined,
  FormOutlined,
} from '@ant-design/icons';
import AnnounceKit from 'announcekit-react';
import axios from 'axios';
import {
  ChartLine, ChatsCircle, Phone, UserSwitch,
} from '@phosphor-icons/react';
import {
  AddressBookSvg, CloudArrowUpSvg, GearSvg, HouseSvg, KeySvg, MailboxSvg, MegaphoneSvg, PaperPlaneTiltSvg, QuestionSvg, SignOutSvg,
  UsersSvg,
} from '../../assets/img/redesign/svgPaths';
import {
  verifyFeatureAccess,
  verifyPatientEngagementAccess,
  verifyPatientRecaptureAccess,
  verifyReceivedInboxAccess,
  verifySentInboxAccess,
  verifyVoiceInboxAccess,
} from '../../global/featureFlags';
import { captureAnalytics } from '../../actions/preload';
import './newNav.scss';
import MenuItems from './MenuItems';

const { Sider } = Layout;

let cableSubscription;

const NewNav = ({
  auth,
  guest,
  createGuest,
  user,
  logout,
  toggle,
  openModal,
  faxSource,
  redirectToCreateGuestAccount,
  loginErrorMessage,
  adminLoginSuccess,
  loginAdmin,
  cable,
  setNumberOfNewReferrals,
  incrementReferralCount,
  decrementReferralCount,
  newReferralsCount,
  history,
  isAdmin,
  referralManagement,
  viewReferralAnalytics,
}) => {
  const [canViewVoiceAi, setCanViewVoiceAi] = useState(false);
  const [canAccessReceivedInbox, setCanAccessReceivedInbox] = useState(true);
  const [canAccessSentInbox, setCanAccessSentInbox] = useState(true);
  const [canAccessVoiceInbox, setCanAccessVoiceInbox] = useState(true);
  const [hasNoFeatures, setHasNoFeatures] = useState(false);
  const [hasReferralPortal, setHasReferralPortal] = useState(false);
  const [canAccessPatientRecapture, setCanAccessPatientRecapture] = useState(false);
  const [canSeeRecaptureUpsell, setCanSeeRecaptureUpsell] = useState(false);
  // Initialized false so a rejected feature-access await can only hide the
  // Patient Engagement entry, never flash it.
  const [canAccessPatientEngagement, setCanAccessPatientEngagement] = useState(false);
  const [hasInboundShares, setHasInboundShares] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(false);
  const announceKitRef = useRef(null);
  const canViewReferralAnalytics = (isAdmin && referralManagement) || viewReferralAnalytics;
  const canViewMairaAnalytics = isAdmin && canViewVoiceAi;

  useEffect(() => {
    const getFeatureAccess = async () => {
      setCanViewVoiceAi(await verifyFeatureAccess('voice_ai'));
      setCanAccessReceivedInbox(await verifyReceivedInboxAccess());
      setCanAccessSentInbox(await verifySentInboxAccess());
      setCanAccessVoiceInbox(await verifyVoiceInboxAccess());
      setHasNoFeatures(await verifyFeatureAccess('no_features'));
      setHasReferralPortal(await verifyFeatureAccess('referral_portal'));
      // Patient Recapture is sourced from the backend-derived m8_patient_recapture
      // localStorage key (admins short-circuit true in User#patient_recapture).
      // Upsell shows for admins whose org has recapture_upsell_flag but does NOT
      // grant patient_recapture access to this user.
      const [hasRecapture, upsellEnabled] = await Promise.all([
        verifyPatientRecaptureAccess(),
        verifyFeatureAccess('recapture_upsell_flag'),
      ]);
      setCanAccessPatientRecapture(hasRecapture);
      setCanSeeRecaptureUpsell(Boolean(upsellEnabled) && !hasRecapture && isAdmin);
      // Patient Engagement access is role-based — backend-derived
      // m8_patient_engagement key (User#patient_engagement).
      setCanAccessPatientEngagement(await verifyPatientEngagementAccess());
    };

    if (user) {
      getFeatureAccess();
    }

    return () => {
      if (cableSubscription) {
        cableSubscription.unsubscribe();
      }
    };
  }, [user, isAdmin]);

  // For non-portal-only orgs, check if they have inbound referral shares
  // to decide whether to show the portal nav icon.
  useEffect(() => {
    if (!hasReferralPortal || hasNoFeatures) return;

    const cached = sessionStorage.getItem('hasPortalAccess');
    if (cached !== null) {
      setHasInboundShares(cached === 'true');
      return;
    }

    axios.get('/api/v1/portal/referrals/has_portal_access')
      .then(({ data }) => {
        setHasInboundShares(data.has_portal_access);
        sessionStorage.setItem('hasPortalAccess', String(data.has_portal_access));
      })
      .catch(() => {
        setHasInboundShares(false);
      });
  }, [hasReferralPortal, hasNoFeatures]);

  useEffect(() => {
    if (auth && !cableSubscription && referralManagement) {
      cableSubscription = cable.subscriptions.create(
        {
          channel: 'ReferralsChannel',
        },
        {
          received: (message) => {
            if (message === 'increment') {
              incrementReferralCount();
            } else if (message === 'decrement') {
              decrementReferralCount();
            } else {
              setNumberOfNewReferrals(message);
            }
          },
        },
      );
    }

    return () => {
      if (cableSubscription) {
        cableSubscription.unsubscribe();
      }
    };
  }, [auth, referralManagement]);

  useEffect(() => {
    if (loginErrorMessage) {
      /* global toastr */
      toastr.error(loginErrorMessage);
    }
  }, [loginErrorMessage]);

  useEffect(() => {
    if (adminLoginSuccess) {
      const win = window.open(`${window.location.origin}/admin`, '_blank');
      win.opener = null;
    }
  }, [adminLoginSuccess]);

  useEffect(() => {
    if (announceKitRef.current && !unreadNotifications) {
      announceKitRef.current.unread().then((unread) => {
        if (unread !== 0) {
          setUnreadNotifications(true);
        }
      });
    }
  }, [announceKitRef.current]);

  const isOnChromeExt = window.location.pathname.includes('/app/chrome/');

  const onMobile = window.innerWidth < 800;

  const openNewUpdates = () => {
    announceKitRef.current.open();
  };

  // UX Decision: When hasNoFeatures is true, we show all nav buttons as disabled rather than
  // hiding them. This lets users see what features exist in the full product, encouraging
  // potential upgrades while clearly indicating they don't have access.
  const shouldDisableNavButtons = hasNoFeatures;

  const withDisabled = (button, allowWhenNoFeatures = false) => (button ? {
    ...button,
    disabled: shouldDisableNavButtons && !allowWhenNoFeatures,
  } : button);

  // Portal-only orgs (no_features + referral_portal) always see the portal.
  // Regular orgs only see it if they have active inbound referral shares.
  const showPortal = hasReferralPortal && (hasNoFeatures || hasInboundShares);

  const referralPortalButton = showPortal && withDisabled({
    label: 'Referral Portal',
    link: '/app/referral_portal',
    icon: <FormOutlined style={{ paddingBottom: 2 }} />,
    key: 'referral_portal',
    onClick: () => history.push('/app/referral_portal'),
  }, true);
  const showReferralPortalAtTop = hasNoFeatures && showPortal;
  const showReferralPortalAfterReferrals = !hasNoFeatures && showPortal;

  const navButtons = [
    showReferralPortalAtTop && referralPortalButton,
    withDisabled({
      label: 'Home',
      link: '/app',
      icon: HouseSvg,
      key: 'home',
      onClick: () => history.push('/app'),
    }),
    withDisabled({
      label: 'Uploads',
      link: '/app/uploads',
      icon: CloudArrowUpSvg,
      key: 'uploads',
      onClick: () => history.push('/app/uploads'),
    }),
    withDisabled((hasNoFeatures || canAccessSentInbox) && {
      label: 'Sent',
      link: '/app/sent',
      icon: PaperPlaneTiltSvg,
      key: 'sent',
      onClick: () => history.push('/app/sent'),
    }),
    withDisabled((hasNoFeatures || canAccessReceivedInbox) && {
      label: 'Inbox',
      link: '/app/received',
      icon: MailboxSvg,
      key: 'inbox',
      onClick: () => history.push('/app/received'),
    }),
    withDisabled({
      label: 'Assigned',
      link: '/app/assigned',
      icon: <ExceptionOutlined />,
      key: 'assigned',
      onClick: () => history.push('/app/assigned'),
    }),
    withDisabled((hasNoFeatures || referralManagement) && {
      label: 'Referrals',
      link: '/app/referred',
      icon: UsersSvg,
      key: 'referrals',
      badgeCount: null,
      onClick: () => history.push('/app/referred'),
    }),
    showReferralPortalAfterReferrals && referralPortalButton,
    withDisabled((hasNoFeatures || canAccessVoiceInbox) && {
      label: 'Voice',
      link: '/app/voice_inbox',
      icon: <Phone size={24} weight="fill" />,
      key: 'voice_inbox',
      onClick: () => history.push('/app/voice_inbox'),
    }),
    withDisabled((hasNoFeatures || canViewReferralAnalytics || canViewMairaAnalytics) && {
      label: 'Analytics',
      link: '/app/analytics',
      icon: <ChartLine size={24} weight="fill" />,
      key: 'analytics',
      onClick: () => history.push('/app/analytics'),
    }),
    canAccessPatientRecapture && withDisabled({
      label: 'Recapture',
      link: '/app/patient-recapture',
      icon: <UserSwitch size={24} weight="fill" />,
      key: 'patient-recapture',
      onClick: () => history.push('/app/patient-recapture'),
    }),
    canSeeRecaptureUpsell && !hasNoFeatures && withDisabled({
      label: 'Recapture',
      link: '/app/recapture',
      icon: <UserSwitch size={24} weight="fill" />,
      key: 'recapture-sales',
      onClick: () => history.push('/app/recapture'),
    }),
    canAccessPatientEngagement && withDisabled({
      label: 'Patient Engagement',
      link: '/app/patient-engagement',
      icon: <ChatsCircle size={24} weight="fill" />,
      key: 'patient-engagement',
      onClick: () => {
        captureAnalytics('patient_engagement_nav_clicked');
        history.push('/app/patient-engagement');
      },
    }),
    !hasNoFeatures && withDisabled({
      label: 'Contacts',
      link: '/app/contacts',
      icon: AddressBookSvg,
      key: 'contacts',
      onClick: () => history.push('/app/contacts'),
    }),
    withDisabled({
      label: 'Updates',
      icon: MegaphoneSvg,
      key: 'updates',
      dot: unreadNotifications,
      onClick: () => {
        openNewUpdates();
        setUnreadNotifications(false);
      },
    }, true),
    // Portal-only partner orgs get the admin-only portal settings page instead
    // of the regular app settings (the page itself and its API are also gated
    // to admins, so non-admins never see the entry).
    hasNoFeatures && showPortal && isAdmin && withDisabled({
      label: 'Settings',
      link: '/app/referral_portal/settings',
      icon: GearSvg,
      key: 'portal-settings',
      onClick: () => history.push('/app/referral_portal/settings'),
    }, true),
    !hasNoFeatures && withDisabled({
      label: 'Settings',
      link: '/app/user',
      icon: GearSvg,
      key: 'settings',
      onClick: () => history.push('/app/user'),
    }),
    !hasNoFeatures && withDisabled({
      label: 'Guide',
      link: '/app/guide',
      icon: QuestionSvg,
      key: 'guide',
      style: { position: 'absolute', bottom: '170px', width: '100%' },
      onClick: () => history.push('/app/guide'),
    }),
    !hasNoFeatures && withDisabled({
      label: 'Admin',
      icon: KeySvg,
      noHighlightOnSelect: true,
      key: 'admin-login',
      style: { position: 'absolute', bottom: '110px', width: '100%' },
      onClick: loginAdmin,
    }),
    withDisabled({
      label: 'Logout',
      icon: SignOutSvg,
      noHighlightOnSelect: true,
      key: 'sign-out',
      onClick: logout,
    }, true),
  ].filter(Boolean);

  const announceKitUser = auth && user?.uid ? {
    id: user.uid,
    email: user.uid,
    name: user?.fullName,
  } : undefined;

  return (
  // hide the new nav bar before logging in and completing the walkthrough or if the user is on mobile
    (auth && window.location.pathname !== '/app/walkthrough' && !onMobile && !isOnChromeExt)
      && (
    // z-index set so nav will always be on top of the inbox sidebars that have a z-index of 1000
        <div className="new-nav" style={{ height: '100%', position: 'absolute', zIndex: 900 }}>
          <Sider id="sidebar-nav" collapsible collapsed={true} trigger={null}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '100%',
            }}
            >
              <MenuItems buttons={navButtons} />
            </div>
            <AnnounceKit ref={announceKitRef} widget="https://releasenotes.medsender.com/widgets/v2/3FvLNK" user={announceKitUser} />
          </Sider>
        </div>
      )
  );
};

NewNav.defaultProps = {
  user: '',
  faxSource: '',
};

NewNav.propTypes = {
  auth: PropTypes.bool.isRequired,
  guest: PropTypes.bool.isRequired,
  createGuest: PropTypes.bool.isRequired,
  user: PropTypes.object,
  logout: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  faxSource: PropTypes.string,
  redirectToCreateGuestAccount: PropTypes.func.isRequired,
  adminLoginSuccess: PropTypes.bool.isRequired,
  loginErrorMessage: PropTypes.string.isRequired,
  loginAdmin: PropTypes.func.isRequired,
  cable: PropTypes.any.isRequired,
  setNumberOfNewReferrals: PropTypes.func.isRequired,
  incrementReferralCount: PropTypes.func.isRequired,
  decrementReferralCount: PropTypes.func.isRequired,
  newReferralsCount: PropTypes.number.isRequired,
  history: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  referralManagement: PropTypes.bool.isRequired,
  viewReferralAnalytics: PropTypes.bool.isRequired,
};

export default NewNav;
